import { MouseEvent } from 'react';

import { useCurrency } from '@/lib/entities/currency';
import { IExtendedOwnVillas } from '@/lib/entities/page';

import { modal, FeedBackModalNew } from '@/lib/shared/components/Modal';
import { H3, P3, P2 } from '@/lib/shared/components/Typography';
import { getWordEnding } from '@/lib/shared/utils/get-word-ending';
import { toMoney } from '@/lib/shared/utils/to-money';

import { useTranslation } from 'next-i18next';
import Image from 'next/image';

import styles from './VillaCard.module.scss';

import { Button } from '../../Buttons';

type Props = IExtendedOwnVillas & {
  onClick: (e: MouseEvent<HTMLDivElement>) => void;
  keyPrefix: string;
  customWhatsappUrl?: string;
};

export const VillaCard = ({
  bedrooms,
  district,
  image,
  name,
  price,
  region,
  description,
  secondaryImage,
  priceFrom,
  defaultPrice,
  onClick,
  keyPrefix,
  customWhatsappUrl,
}: Props) => {
  const { currency } = useCurrency();
  const {
    t,
    i18n: { language },
  } = useTranslation('index');
  const openModal = (value: string) => {
    modal.open(
      <FeedBackModalNew
        leadType="SALE"
        title="seeTheVilla"
        comment={`Запрос просмотра объекта ${value}`}
        eventName={['sale']}
        sourceForm="portfolio villas landing"
        customWhatsappUrl={customWhatsappUrl}
      />,
      { isFeedBackNew: true }
    );
  };
  return (
    <div className={styles.card} onClick={onClick}>
      <div className={styles.card__left}>
        <Image
          className={styles.card__primary}
          src={image}
          loader={({ src }) => src}
          alt="primary image"
          width={585}
          height={297}
        />
        <div className={styles.card__info}>
          <H3>{name}</H3>
          <P3 className={styles.card__description}>
            {t(`${keyPrefix}.` + description)}
          </P3>
          <P2 className={styles.card__district}>
            {t('locations.' + region)}, {t('locations.' + district)}{' '}
            <span>|</span> {bedrooms}{' '}
            {getWordEnding(
              bedrooms,
              [
                t('declinable.bedroom'),
                t('declinable.bedroomsFew'),
                t('declinable.bedroomsMore'),
              ],
              language
            )}
          </P2>
        </div>
        <div className={styles.card__divider} />
        <div className={styles.card__price}>
          {price ? (
            <H3>
              {priceFrom ? t('prepositions.from') : ''} {currency.symbol}{' '}
              {toMoney(price[currency.value])}
            </H3>
          ) : (
            <H3>
              {priceFrom ? t('prepositions.from') : ''}
              {' ฿ '} {toMoney(defaultPrice)}
            </H3>
          )}
          <Button
            className={styles.card__order}
            as="button"
            size="lg"
            label={t('orderViewing')}
            theme="light-invert"
            onClick={() => openModal(name)}
          />
        </div>
      </div>
      <div className={styles.card__right}>
        <Image
          className={styles.card__secondary}
          src={secondaryImage}
          loader={({ src }) => src}
          alt="secondary image"
          width={364}
          height={558}
        />
      </div>
    </div>
  );
};
